/**
 * HostApplicationDialog — apply to become an approved host, or see your status.
 *
 * Used by Home.jsx in place of the host-create dialog when user.can_host is false.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Clock, XCircle, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { API } from '../App';

export const HostApplicationDialog = ({ open, onOpenChange, onApproved }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [experience, setExperience] = useState('');
  const [statusInfo, setStatusInfo] = useState(null); // { can_host, application }

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/host-applications/me`)
      .then(r => { if (mounted) setStatusInfo(r.data); })
      .catch(() => { if (mounted) setStatusInfo({ can_host: false, application: null }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [open]);

  const submit = async () => {
    if (reason.trim().length < 20) {
      toast.error('Tell us a bit more (at least 20 characters)');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/host-applications`, {
        reason: reason.trim(),
        experience: experience.trim(),
      });
      if (r.data?.status === 'already_approved') {
        toast.success("You're already approved — opening the host form.");
        onApproved?.();
        onOpenChange(false);
        return;
      }
      toast.success('Application submitted! Admin will review and notify you.');
      setStatusInfo({ can_host: false, application: r.data.application });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatus = () => {
    const app = statusInfo?.application;
    if (!app) return null;
    if (app.status === 'pending') {
      return (
        <div data-testid="host-app-pending" className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <p className="text-sm font-bold text-amber-300">Application Pending Review</p>
          </div>
          <p className="text-xs text-amber-100/80 leading-relaxed">
            Submitted {new Date(app.created_at).toLocaleDateString()}. Admin typically reviews within 24-48 hours.
            You'll be notified the moment you're approved — then the Host button opens the room creator.
          </p>
          <div className="text-[11px] text-slate-400 bg-slate-900/40 rounded-lg p-2 mt-2">
            <span className="text-slate-500">Your pitch:</span> {app.reason}
          </div>
        </div>
      );
    }
    if (app.status === 'approved') {
      return (
        <div data-testid="host-app-approved" className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <p className="text-sm font-bold text-emerald-300">You're an Approved Host!</p>
          </div>
          <p className="text-xs text-emerald-100/80">Tap the Host button to list a prize.</p>
        </div>
      );
    }
    if (app.status === 'denied') {
      return (
        <div data-testid="host-app-denied" className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-400" />
            <p className="text-sm font-bold text-red-300">Application Denied</p>
          </div>
          {app.denial_reason && (
            <p className="text-xs text-red-100/80">Reason: {app.denial_reason}</p>
          )}
          <p className="text-[11px] text-slate-400 pt-1">You may submit a new application below.</p>
        </div>
      );
    }
    return null;
  };

  const canApply = !loading && (
    !statusInfo?.application ||
    statusInfo.application.status === 'denied'
  ) && !statusInfo?.can_host;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="host-application-dialog" className="bg-slate-900 border-slate-700 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            Apply to Host
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3">
              <p className="text-xs text-purple-200/90 leading-relaxed">
                Hosting is admin-approved to keep listings high-quality and protect buyers.
                Tell us about what you'd like to list and your selling experience — review takes 24-48 hours.
              </p>
            </div>

            {renderStatus()}

            {canApply && (
              <>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">
                    What would you list? <span className="text-red-400">*</span>
                  </label>
                  <Textarea
                    data-testid="host-app-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={600}
                    rows={4}
                    placeholder="e.g. Vintage Pokemon TCG holos (Charizard, Blastoise, Venusaur 1999 base set), all PSA-graded 8+. About 30 cards I'm comfortable parting with."
                    className="bg-slate-800 border-slate-700 text-white text-sm"
                  />
                  <p className="text-[10px] text-slate-500 mt-0.5">{reason.length}/600 — min 20 characters</p>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">
                    Selling experience (optional)
                  </label>
                  <Textarea
                    data-testid="host-app-experience"
                    value={experience}
                    onChange={(e) => setExperience(e.target.value)}
                    maxLength={600}
                    rows={3}
                    placeholder="e.g. eBay 250+ feedbacks 99.6% positive, sold on TCGplayer for 4 years, active in Reddit /r/PokemonTCG"
                    className="bg-slate-800 border-slate-700 text-white text-sm"
                  />
                </div>

                <Button
                  data-testid="host-app-submit"
                  onClick={submit}
                  disabled={submitting || reason.trim().length < 20}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {submitting ? 'Submitting…' : 'Submit Application'}
                </Button>

                <p className="text-[10px] text-slate-600 text-center">
                  Approval is at admin's discretion. Re-apply anytime if denied.
                </p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HostApplicationDialog;
